/**
 * Text Processing Utilities
 * 
 * Functions for processing text, hashtags, mentions, and formatting
 */

/**
 * Capitalize first letter of a string
 * @param {string} val - String to capitalize
 * @returns {string} Capitalized string
 */
export function capitalizeFirstLetter(val) {
  if (!val) return "";
  const str = String(val).replace(/_/g, " ").toLowerCase();
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Extract hashtags from a string
 * @param {string} text - Text to extract hashtags from
 * @returns {string[]} Array of hashtag names (without #)
 */
export function extractHashtagsFromString(text) {
  const tags = [];
  const words = text.split(" ");
  words.forEach((word) => {
    if (word.startsWith("#")) tags.push(word.substring(1));
  });

  return tags;
}

/**
 * Render mentions in text as clickable links
 * @param {string} text - Text with mentions
 * @param {Array} mentionedUsers - Array of mentioned users
 * @returns {string} HTML string with clickable mention links
 */
export function renderMentionsInText(text, mentionedUsers = []) {
  if (!text || !mentionedUsers || mentionedUsers.length === 0) {
    return text;
  }

  const words = text.split(" ");
  let reconstructedText = "";

  words.forEach((word, index) => {
    const space = index === 0 ? "" : " ";

    if (word.startsWith("@")) {
      // Extract the name after @
      const mentionName = word.substring(1);

      // Find matching user in mentionedUsers array
      const matchedUser = mentionedUsers.find(
        (user) =>
          user.name === mentionName ||
          `${user.firstName} ${user.lastName}` === mentionName ||
          user.username === mentionName,
      );

      if (matchedUser) {
        // Create clickable link to friend profile
        word = `${space}<a href="/friendprofile/${matchedUser.id}" class="mention-link" style="text-decoration: none; font-weight: 500;">${word}</a>`;
      } else {
        // If no match found, keep as plain text
        word = `${space}${word}`;
      }
      reconstructedText += word;
    } else {
      reconstructedText += `${space}${word}`;
    }
  });

  return reconstructedText;
}

/**
 * Normalize mention data from various sources (PostTag, CommentMention, etc.)
 * to a unified structure
 * @param {Array} mentions - Array of mention objects from different sources
 * @returns {Array} Normalized mention objects
 */
function normalizeMentions(mentions = []) {
  if (!mentions || mentions.length === 0) return [];

  return mentions.map((mention) => {
    // Handle PostTag structure (has user, group, page relations)
    if (mention.user) {
      return {
        id: mention.user.id,
        entityType: 'user',
        tagText: mention.tagText || `@${mention.user.username || ''}`,
        mentionValue: mention.user.username || '',
        displayName: `${mention.user.firstName || ''} ${mention.user.lastName || ''}`.trim() || mention.user.username || '',
        username: mention.user.username,
        userId: mention.user.id,
        groupId: null,
        pageId: null,
      };
    }

    if (mention.group) {
      return {
        id: mention.group.id,
        entityType: 'group',
        tagText: mention.tagText || `@${mention.group.slug || mention.group.name || ''}`,
        mentionValue: mention.group.slug || mention.group.name?.replace(/\s+/g, '').toLowerCase() || '',
        displayName: mention.group.name || '',
        slug: mention.group.slug,
        userId: null,
        groupId: mention.group.id,
        pageId: null,
      };
    }

    if (mention.page) {
      return {
        id: mention.page.id,
        entityType: 'page',
        tagText: mention.tagText || `@${mention.page.username || mention.page.name || ''}`,
        mentionValue: mention.page.username || mention.page.name?.replace(/\s+/g, '').toLowerCase() || '',
        displayName: mention.page.name || '',
        username: mention.page.username,
        userId: null,
        groupId: null,
        pageId: mention.page.id,
      };
    }

    // Handle CommentMention structure (has mentionedUserId, mentionedPageId, mentionedGroupId)
    if (mention.mentionedUserId || mention.mentionedUser) {
      const user = mention.mentionedUser || {};
      return {
        id: mention.mentionedUserId || user.id,
        entityType: 'user',
        tagText: mention.tagText || `@${user.username || ''}`,
        mentionValue: user.username || '',
        displayName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username || '',
        username: user.username,
        userId: mention.mentionedUserId || user.id,
        groupId: null,
        pageId: null,
      };
    }

    if (mention.mentionedGroupId || mention.mentionedGroup) {
      const group = mention.mentionedGroup || {};
      return {
        id: mention.mentionedGroupId || group.id,
        entityType: 'group',
        tagText: mention.tagText || `@${group.slug || group.name || ''}`,
        mentionValue: group.slug || group.name?.replace(/\s+/g, '').toLowerCase() || '',
        displayName: group.name || '',
        slug: group.slug,
        userId: null,
        groupId: mention.mentionedGroupId || group.id,
        pageId: null,
      };
    }

    if (mention.mentionedPageId || mention.mentionedPage) {
      const page = mention.mentionedPage || {};
      return {
        id: mention.mentionedPageId || page.id,
        entityType: 'page',
        tagText: mention.tagText || `@${page.username || page.name || ''}`,
        mentionValue: page.username || page.name?.replace(/\s+/g, '').toLowerCase() || '',
        displayName: page.name || '',
        username: page.username,
        userId: null,
        groupId: null,
        pageId: mention.mentionedPageId || page.id,
      };
    }

    // Handle already normalized structure or direct user/group/page objects
    if (mention.entityType) {
      return {
        id: mention.id,
        entityType: mention.entityType,
        tagText: mention.tagText || `@${mention.mentionValue || mention.username || mention.slug || ''}`,
        mentionValue: mention.mentionValue || mention.username || mention.slug || '',
        displayName: mention.displayName || mention.name || '',
        username: mention.username,
        slug: mention.slug,
        userId: mention.userId || mention.id,
        groupId: mention.groupId,
        pageId: mention.pageId,
      };
    }

    // Fallback: assume it's a user if it has id
    if (mention.id) {
      return {
        id: mention.id,
        entityType: 'user',
        tagText: mention.tagText || `@${mention.username || mention.mentionValue || ''}`,
        mentionValue: mention.username || mention.mentionValue || '',
        displayName: mention.displayName || `${mention.firstName || ''} ${mention.lastName || ''}`.trim() || mention.username || '',
        username: mention.username,
        userId: mention.id,
        groupId: null,
        pageId: null,
      };
    }

    return null;
  }).filter(Boolean);
}

/**
 * Render hashtags and mentions in text as segments
 * @param {string} text - Text with hashtags and mentions
 * @param {Array} mentionedUsers - Array of mentioned users/tags (PostTag, CommentMention, etc.)
 * @param {Array} allFriends - Array of all friends (fallback for unmatched mentions)
 * @returns {Array} Array of segment objects with type, value, and link
 */
export function renderHashtagsAndMentionsInText(text, mentionedUsers = [], allFriends = []) {
  if (!text) return [];

  // Normalize mentions to unified structure
  const normalizedMentions = normalizeMentions(mentionedUsers);

  // Helper function to process a single word into a segment
  const processWord = (word) => {
    if (!word) return null;

    let segment = { type: 'text', value: word, link: null };

    if (word.startsWith("#")) {
      // Handle hashtags
      const tagName = word.substring(1).trim();
      if (tagName) {
        segment = {
          type: 'hashtag',
          value: word,
          link: `/posts/tags/${tagName}`,
        };
      }
    } else if (word.startsWith("@")) {
      // Handle mentions
      let mentionName = word.substring(1).trim();
      mentionName = mentionName.replace(/[.,!?;:]+$/, '').toLowerCase();

      if (mentionName) {
        let matchedMention = null;

        // 1. Try to find matching mention in normalized mentions array
        if (normalizedMentions && normalizedMentions.length > 0) {
          matchedMention = normalizedMentions.find(
            (mention) => {
              const tagTextMatch = mention.tagText?.toLowerCase().replace('@', '') === mentionName;
              const usernameMatch = mention.username?.toLowerCase() === mentionName;
              const mentionValueMatch = mention.mentionValue?.toLowerCase() === mentionName;
              const slugMatch = mention.slug?.toLowerCase() === mentionName;

              return tagTextMatch || usernameMatch || mentionValueMatch || slugMatch;
            }
          );
        }

        // 2. If not found, try to find in allFriends (fallback for users only)
        if (!matchedMention && allFriends && allFriends.length > 0) {
          const foundFriend = allFriends.find((friend) => {
            const friendData = friend.friend || friend;
            const friendUsername = friendData.username?.toLowerCase();
            const friendName = `${friendData.firstName || ''} ${friendData.lastName || ''}`.trim().toLowerCase();

            return (
              friendUsername === mentionName ||
              friendName === mentionName ||
              friendData.firstName?.toLowerCase() === mentionName ||
              friendData.lastName?.toLowerCase() === mentionName
            );
          });

          if (foundFriend) {
            const friendData = foundFriend.friend || foundFriend;
            matchedMention = {
              id: friendData.id,
              entityType: 'user',
              userId: friendData.id,
              groupId: null,
              pageId: null,
            };
          }
        }

        // 3. Generate link based on matched mention
        if (matchedMention) {
          let link = null;

          // Determine entity type and generate appropriate link
          if (matchedMention.entityType === 'group' || matchedMention.groupId) {
            link = `/group-detail/${matchedMention.groupId || matchedMention.id}`;
          } else if (matchedMention.entityType === 'page' || matchedMention.pageId) {
            link = `/page-view/${matchedMention.pageId || matchedMention.id}`;
          } else if (matchedMention.entityType === 'user' || matchedMention.userId || matchedMention.id) {
            link = `/friendprofile/${matchedMention.userId || matchedMention.id}`;
          }

          if (link) {
            segment = {
              type: 'mention',
              value: word,
              link: link,
            };
          } else {
            segment = { type: 'text', value: word, link: null };
          }
        } else {
          segment = { type: 'text', value: word, link: null };
        }
      }
    }

    return segment;
  };

  // First, split by newlines (\r\n or \n) to preserve line breaks
  const lines = text.split(/\r?\n/);
  const segments = [];

  lines.forEach((line, lineIndex) => {
    // Split each line by spaces
    const words = line.split(/[ \t]+/);

    words.forEach((word, wordIndex) => {
      const segment = processWord(word);
      if (segment) {
        segments.push(segment);
        // Add space between words (not after the last word in a line)
        if (wordIndex < words.length - 1) {
          segments.push({ type: 'text', value: ' ', link: null });
        }
      }
    });

    // Add newline segment between lines (not after the last line)
    if (lineIndex < lines.length - 1) {
      segments.push({ type: 'newline', value: '\n', link: null });
    }
  });

  return segments;
}

/**
 * True if code point is Arabic script (incl. presentation forms).
 * @param {number} cp
 */
function isArabicScriptCodePoint(cp) {
  return (
    (cp >= 0x0600 && cp <= 0x06ff) ||
    (cp >= 0x0750 && cp <= 0x077f) ||
    (cp >= 0x08a0 && cp <= 0x08ff) ||
    (cp >= 0xfb50 && cp <= 0xfdff) ||
    (cp >= 0xfe70 && cp <= 0xfeff)
  );
}

/**
 * Latin letters (basic + extended Latin for French, etc.)
 * @param {number} cp
 */
function isLatinLetterCodePoint(cp) {
  return (
    (cp >= 0x0041 && cp <= 0x005a) ||
    (cp >= 0x0061 && cp <= 0x007a) ||
    (cp >= 0x00c0 && cp <= 0x024f) ||
    (cp >= 0x1e00 && cp <= 0x1eff)
  );
}

/**
 * Direction for user-generated content so Arabic reads RTL even when the UI locale is en/fr.
 * Uses letter counts: if Arabic letters exist and are not fewer than Latin letters → rtl.
 * @param {string} text
 * @returns {'rtl'|'ltr'}
 */
export function getContentTextDirection(text) {
  if (!text || typeof text !== 'string') return 'ltr';
  let arabic = 0;
  let latin = 0;
  for (const ch of text) {
    const cp = ch.codePointAt(0);
    if (isArabicScriptCodePoint(cp)) arabic += 1;
    else if (isLatinLetterCodePoint(cp)) latin += 1;
  }
  if (arabic === 0) return 'ltr';
  if (latin === 0) return 'rtl';
  return arabic >= latin ? 'rtl' : 'ltr';
}

/**
 * Classify a whitespace-delimited token by dominant script (letters only inside the token).
 * @returns {'arabic'|'latin'|'neutral'}
 */
function classifyWordScript(word) {
  let ar = 0;
  let lat = 0;
  for (const ch of word) {
    const cp = ch.codePointAt(0);
    if (isArabicScriptCodePoint(cp)) ar += 1;
    else if (isLatinLetterCodePoint(cp)) lat += 1;
  }
  if (ar === 0 && lat === 0) return 'neutral';
  if (ar > lat) return 'arabic';
  if (lat > ar) return 'latin';
  return 'neutral';
}

/**
 * Direction for one paragraph of post description.
 * Mixed Arabic/Latin uses whichever side has more "words" (tokens with a dominant script).
 * Ties or only neutral tokens fall back to letter counts via getContentTextDirection.
 * @param {string} text
 * @returns {'rtl'|'ltr'}
 */
export function getParagraphTextDirection(text) {
  if (!text || typeof text !== 'string') return 'ltr';
  const trimmed = text.trim();
  if (!trimmed) return 'ltr';

  const tokens = trimmed.split(/\s+/).filter(Boolean);
  let arabicWords = 0;
  let latinWords = 0;
  for (const token of tokens) {
    const c = classifyWordScript(token);
    if (c === 'arabic') arabicWords += 1;
    else if (c === 'latin') latinWords += 1;
  }
  if (arabicWords > latinWords) return 'rtl';
  if (latinWords > arabicWords) return 'ltr';
  return getContentTextDirection(trimmed);
}

/**
 * Split post body into blocks for per-block `dir`.
 * - Blank line(s) between lines → paragraph boundaries.
 * - Otherwise, each single newline is its own block (line-by-line direction).
 * @param {string} text
 * @returns {string[]}
 */
export function splitPostDescriptionIntoParagraphs(text) {
  if (!text || typeof text !== 'string') return [];
  const normalized = text.replace(/\r\n/g, '\n');
  let parts;
  if (/\n[ \t]*\n/.test(normalized)) {
    parts = normalized.split(/\n[ \t]*\n+/);
  } else if (normalized.includes('\n')) {
    parts = normalized.split('\n');
  } else {
    return [normalized];
  }
  return parts
    .map((p) => p.replace(/^\n+|\n+$/g, '').trimEnd())
    .filter((p) => p.trim().length > 0);
}

